import { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { AppContext } from "../App";
import { getInlineLoader } from "../Helpers";
import { IoChevronBack, IoChevronForward, IoShieldCheckmarkOutline, IoLeafOutline } from "react-icons/io5";
import { GiMeat, GiCheckMark } from "react-icons/gi";
import { FaGlobeAfrica, FaShippingFast, FaArrowRight } from "react-icons/fa";
import logo from "../assets/logo.png";

// Assets
import bannerMutton from "../assets/banner5.jpg";
import bannerAvocado from "../assets/banner1.jpg";
import bannerSustain from "../assets/export.jpg";
import bannerCert from "../assets/quality.jpg";

export default function HomeServicesShowcase() {
  const { navTo } = useContext(AppContext);
  const [isReady, setIsReady] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);

  const slides = useMemo(() => [
    {
      image: bannerMutton,
      title: "Premium Mutton",
      text: "Hygienic processing of fresh and frozen mutton with custom cuts, tailored for Middle Eastern markets.",
      icon: <GiMeat />,
      subItem: "mutton",
      tag: "Halal Certified",
      accent: "#B8860B"
    },
    {
      image: bannerAvocado,
      title: "Fresh Avocados",
      text: "Export-grade Hass avocados sourced from sustainable Tanzanian farms and graded for global quality.",
      icon: <IoLeafOutline />,
      subItem: "avocados",
      tag: "Global Gap",
      accent: "#2E8B57"
    },
    {
      image: bannerCert,
      title: "Quality Compliance",
      text: "Fully compliant with Tanzania Meat Board and international safety standards from farm to table.",
      icon: <IoShieldCheckmarkOutline />,
      subItem: "certifications",
      tag: "Safe & Secure",
      accent: "#4682B4"
    },
    {
      image: bannerSustain,
      title: "Global Export",
      text: "Efficient cold-chain logistics ensuring our agricultural products reach international ports in peak condition.",
      icon: <FaGlobeAfrica />,
      subItem: "logistics",
      tag: "Middle East Focus",
      accent: "#1A242F"
    },
  ], []);

  const handleNext = useCallback(() => {
    setSlideIndex((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const handlePrev = useCallback(() => {
    setSlideIndex((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    setIsReady(true);
    const interval = setInterval(handleNext, 8000);
    return () => clearInterval(interval);
  }, [handleNext]);

  if (!isReady) return <div className="loading-state">{getInlineLoader()}</div>;

  return (
    <section className="modern-showcase py-5">
      <div className="container">
        
        {/* Minimalist Header */}
        <div className="row mb-5 align-items-end">
          <div className="col-lg-7 mt-3 d-flex gap-3 gap-md-2 align-items-center" data-aos="fade-up">
            <img src={logo} width={100} />
            <h6 className="display-4 font-bold text-primary mb-0 mt-3">THAI H&K TRADING</h6>
          </div>
          <div className="col-lg-5 mt-3" data-aos="fade-up" data-aos-delay="100">
            <p className="text-muted fs-6 mb-0 border-start ps-4">
              THAI H&K TRADING specializes in processing and exporting premium products, 
              delivering internationally compliant quality with every shipment.
            </p>
          </div>
        </div>

        <div className="row g-0 rounded-5 shadow-premium overflow-hidden bg-white">
          
          {/* Left Side: Dynamic Content Info */}
          <div className="col-lg-5 p-5 d-flex flex-column justify-content-center bg-soft">
            <div className="mb-4">
               {slides.map((s, i) => (
                 <div key={i} className={`fade-item ${i === slideIndex ? 'active' : ''}`}>
                    <div className="icon-main mb-4" style={{ color: s.accent }}>{s.icon}</div>
                    <span className="badge-premium mb-3" style={{ borderLeftColor: s.accent }}>{s.tag}</span>
                    <h3 className="h1 font-bold text-navy mb-3">{s.title}</h3>
                    <p className="text-muted lead-sm mb-4">{s.text}</p>
                    <button 
                      onClick={() => navTo({ item: "services", subItem: s.subItem })}
                      className="btn-link-modern"
                    >
                      Learn More <FaArrowRight className="ms-2" />
                    </button>
                 </div>
               ))}
            </div>

            {/* Pagination Controls */}
            <div className="d-flex align-items-center gap-4 mt-auto">
              <div className="d-flex gap-2">
                <button onClick={handlePrev} className="btn-circle-outline"><IoChevronBack /></button>
                <button onClick={handleNext} className="btn-circle-outline"><IoChevronForward /></button>
              </div>
              <div className="text-muted small fw-bold">
                0{slideIndex + 1} / 0{slides.length}
              </div>
            </div>
          </div>

          {/* Right Side: Visual Image Canvas */}
          <div className="col-lg-7 position-relative showcase-canvas">
            {slides.map((slide, index) => (
              <div 
                key={index} 
                className={`canvas-slide ${index === slideIndex ? 'active' : ''}`}
                style={{ backgroundImage: `url(${slide.image})` }}
              >
                <div className="canvas-overlay"></div>
              </div>
            ))}
            
            {/* Hover Nav: Mini Selectors */}
            <div className="mini-selectors">
               {slides.map((_, i) => (
                 <div 
                  key={i} 
                  className={`mini-dot ${i === slideIndex ? 'active' : ''}`}
                  onClick={() => setSlideIndex(i)}
                 />
               ))}
            </div>
          </div>

        </div>

        {/* Global CTA */}
        <div className="mt-5 text-center">
           <button onClick={() => navTo({ item: "contact_us" })} className="btn-premium">
              Partner With Us Today <FaShippingFast className="ms-2" />
           </button>
        </div>

      </div>

      <style>{`
        .modern-showcase { background: #F8FAFC; }
        .text-navy { color: #8DAE81; !important }
        .text-accent { color: #2E8B57; }
        
        .shadow-premium { box-shadow: 0 40px 100px -20px rgba(0,0,0,0.1); }
        .bg-soft { background: #FFFFFF; }
        
        .icon-main { font-size: 3.5rem; }
        .badge-premium { 
          display: inline-block; padding: 0.4rem 1rem; background: #f1f5f9; 
          border-left: 4px solid #2E8B57; font-size: 0.75rem; font-weight: 800; text-uppercase;
        }

        .fade-item { display: none; }
        .fade-item.active { display: block; animation: fadeInUp 0.6s ease-out forwards; }

        .btn-link-modern {
          background: none; border: none; color: #1E293B; font-weight: 700;
          padding: 0; position: relative; transition: 0.3s;
        }
        .btn-link-modern:hover { color: #2E8B57; transform: translateX(5px); }

        .btn-circle-outline {
          width: 45px; height: 45px; border-radius: 50%; border: 1px solid #E2E8F0;
          background: transparent; color: #1E293B; display: grid; place-items: center;
          transition: 0.3s;
        }
        .btn-circle-outline:hover { background: #1E293B; color: #fff; border-color: #1E293B; }

        .showcase-canvas { min-height: 600px; background: #000; }
        .canvas-slide {
          position: absolute; inset: 0; background-size: cover; background-position: center;
          opacity: 0; transition: opacity 1s ease-in-out, transform 1.5s ease;
          transform: scale(1.1);
        }
        .canvas-slide.active { opacity: 1; transform: scale(1); }
        .canvas-overlay { 
          position: absolute; inset: 0; 
          background: linear-gradient(90deg, rgba(255,255,255,0.1) 0%, transparent 100%); 
        }

        .mini-selectors {
          position: absolute; right: 30px; top: 50%; transform: translateY(-50%);
          display: flex; flex-direction: column; gap: 15px; z-index: 10;
        }
        .mini-dot {
          width: 8px; height: 8px; border-radius: 50%; background: rgba(255,255,255,0.4);
          cursor: pointer; transition: 0.3s;
        }
        .mini-dot.active { background: #fff; transform: scale(1.8); }

        .btn-premium {
          background: #3f7752; color: #fff; padding: 1.2rem 3rem; border: none;
          border-radius: 50px; font-weight: 700; transition: 0.4s;
        }
        .btn-premium:hover { background: #2E8B57; transform: translateY(-5px); box-shadow: 0 20px 40px rgba(46,139,87,0.2); }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 991px) {
          .showcase-canvas { min-height: 400px; }
          .mini-selectors { flex-direction: row; top: auto; bottom: 20px; left: 50%; transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
}